import type { FlightDto, StatsDto } from '@flightlog/shared';
import { formatDistance, formatDuration, localParts, plural } from '../format';
import { haptic } from '../telegram';
import { CONTINENTS } from '../world';
import { MONTHS_FULL } from './StatsCharts';

interface Tier {
  value: number;
  label: string;
}

interface Badge {
  icon: string;
  title: string;
  value: number;
  tiers: Tier[];
}

const n = (value: number) => Math.round(value).toLocaleString('ru-RU');
const tiers = (values: number[], unit: string): Tier[] =>
  values.map((value) => ({ value, label: `${n(value)} ${unit}` }));

function badges(stats: StatsDto): Badge[] {
  const visited = new Set(stats.countriesList.map((item) => item.code.toUpperCase()));
  const continents = CONTINENTS.filter((continent) =>
    continent.codes.some((code) => visited.has(code)),
  ).length;

  return [
    {
      icon: '🌍',
      title: 'Километры',
      value: stats.totalDistanceKm,
      tiers: [
        { value: 10_000, label: '10 000 км' },
        { value: 40_075, label: 'Вокруг Земли' },
        { value: 100_000, label: '100 000 км' },
        { value: 250_000, label: '250 000 км' },
        { value: 384_400, label: 'До Луны' },
        { value: 1_000_000, label: 'Миллион км' },
      ],
    },
    { icon: '✈️', title: 'Рейсы', value: stats.totalFlights, tiers: tiers([10, 25, 50, 100, 250, 500], 'рейсов') },
    { icon: '🗺️', title: 'Страны', value: stats.countries, tiers: tiers([5, 10, 25, 50, 100], 'стран') },
    { icon: '🧭', title: 'Части света', value: continents, tiers: tiers([2, 3, 4, 5, 6], 'из 6') },
    { icon: '🛫', title: 'Авиакомпании', value: stats.airlines, tiers: tiers([5, 10, 25, 50], 'авиакомпаний') },
    { icon: '🏢', title: 'Аэропорты', value: stats.airports, tiers: tiers([10, 25, 50, 100], 'аэропортов') },
    {
      icon: '⏱️',
      title: 'Часы в воздухе',
      value: stats.totalDurationMin / 60,
      tiers: [{ value: 24, label: 'Сутки в воздухе' }, ...tiers([100, 250, 500, 1000], 'часов')],
    },
  ];
}

/** Значки за рубежи: какой уровень взят и сколько до следующего. */
export function Achievements({ stats }: { stats: StatsDto }) {
  const list = badges(stats);
  const earned = list.reduce(
    (sum, badge) => sum + badge.tiers.filter((tier) => badge.value >= tier.value).length,
    0,
  );
  const total = list.reduce((sum, badge) => sum + badge.tiers.length, 0);

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">Достижения</h2>
        <span className="section-note">
          {earned} из {total} значков
        </span>
      </div>
      <div className="badges">
        {list.map((badge) => {
          const done = badge.tiers.filter((tier) => badge.value >= tier.value);
          const last = done[done.length - 1];
          const next = badge.tiers[done.length];
          const from = last?.value ?? 0;
          const progress = next ? (badge.value - from) / (next.value - from) : 1;
          return (
            <div className={done.length > 0 ? 'badge' : 'badge is-locked'} key={badge.title}>
              <div className="badge-top">
                <span className="badge-icon" aria-hidden="true">
                  {badge.icon}
                </span>
                <span className="badge-pips" aria-label={`${done.length} из ${badge.tiers.length}`}>
                  {badge.tiers.map((tier) => (
                    <i key={tier.value} className={badge.value >= tier.value ? 'is-on' : ''} />
                  ))}
                </span>
              </div>
              <span className="badge-title">{badge.title}</span>
              <b className="badge-level">{last ? last.label : 'Пока нет'}</b>
              <div className="badge-track">
                <i style={{ width: `${Math.min(1, progress) * 100}%` }} />
              </div>
              <span className="badge-next">
                {next ? `дальше: ${next.label}` : 'все уровни взяты'}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function dayMonthYear(flight: FlightDto): string {
  const dep = localParts(flight.scheduledDepUtc, flight.dep.tz);
  return `${dep.day} ${dep.month} ${dep.year}`;
}

function pause(days: number): string {
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return `${years} ${plural(years, ['год', 'года', 'лет'])}${months ? ` ${months} мес.` : ''}`;
  }
  if (days >= 60) return `${Math.floor(days / 30)} мес.`;
  return `${days} ${plural(days, ['день', 'дня', 'дней'])}`;
}

interface RecordRow {
  label: string;
  value: string;
  meta: string;
  flight?: FlightDto;
}

function records(stats: StatsDto, flights: FlightDto[]): RecordRow[] {
  const now = Date.now();
  const past = flights
    .filter((flight) => flight.status !== 'cancelled' && new Date(flight.scheduledDepUtc).getTime() <= now)
    .sort((a, b) => a.scheduledDepUtc.localeCompare(b.scheduledDepUtc));
  if (past.length === 0) return [];

  const rows: RecordRow[] = [];
  const route = (flight: FlightDto) => `${flight.dep.iata} → ${flight.arr.iata}`;
  const pick = (better: (a: FlightDto, b: FlightDto) => boolean, list = past) =>
    list.reduce((best, flight) => (better(flight, best) ? flight : best));

  const withDistance = past.filter((flight) => (flight.distanceKm ?? 0) > 0);
  if (withDistance.length > 0) {
    const longest = pick((a, b) => (a.distanceKm ?? 0) > (b.distanceKm ?? 0), withDistance);
    const shortest = pick((a, b) => (a.distanceKm ?? 0) < (b.distanceKm ?? 0), withDistance);
    rows.push({
      label: 'Самый длинный',
      value: route(longest),
      meta: `${formatDistance(longest.distanceKm)} · ${dayMonthYear(longest)}`,
      flight: longest,
    });
    rows.push({
      label: 'Самый короткий',
      value: route(shortest),
      meta: `${formatDistance(shortest.distanceKm)} · ${dayMonthYear(shortest)}`,
      flight: shortest,
    });
  }

  const withDuration = past.filter((flight) => (flight.durationMin ?? 0) > 0);
  if (withDuration.length > 0) {
    const longestAir = pick((a, b) => (a.durationMin ?? 0) > (b.durationMin ?? 0), withDuration);
    rows.push({
      label: 'Дольше всего в воздухе',
      value: `${longestAir.timesEstimated ? '≈ ' : ''}${formatDuration(longestAir.durationMin)}`,
      meta: `${route(longestAir)} · ${dayMonthYear(longestAir)}`,
      flight: longestAir,
    });
  }

  const minutes = (flight: FlightDto) => {
    const [hours, mins] = localParts(flight.scheduledDepUtc, flight.dep.tz).time.split(':').map(Number);
    return (hours ?? 0) * 60 + (mins ?? 0);
  };
  // Вылеты до 4 утра — это ещё ночь, а не «ранний вылет».
  const mornings = past.filter((flight) => minutes(flight) >= 4 * 60);
  if (mornings.length > 0) {
    const earliest = pick((a, b) => minutes(a) < minutes(b), mornings);
    rows.push({
      label: 'Самый ранний вылет',
      value: localParts(earliest.scheduledDepUtc, earliest.dep.tz).time,
      meta: `${route(earliest)} · ${dayMonthYear(earliest)}`,
      flight: earliest,
    });
  }

  const airline = stats.topAirlines[0];
  if (airline) {
    rows.push({
      label: 'Любимая авиакомпания',
      value: airline.name,
      meta: `${airline.flights} ${plural(airline.flights, ['рейс', 'рейса', 'рейсов'])} · ${formatDistance(airline.distanceKm)}`,
    });
  }

  const airport = stats.topAirports[0];
  if (airport) {
    rows.push({
      label: 'Любимый аэропорт',
      value: `${airport.iata} · ${airport.city}`,
      meta: `${airport.flights} ${plural(airport.flights, ['раз', 'раза', 'раз'])}`,
    });
  }

  const topRoute = stats.topRoutes[0];
  if (topRoute && topRoute.flights > 1) {
    rows.push({
      label: 'Частый маршрут',
      value: `${topRoute.depIata} → ${topRoute.arrIata}`,
      meta: `${topRoute.flights} ${plural(topRoute.flights, ['раз', 'раза', 'раз'])}`,
    });
  }

  const month = stats.byMonth.reduce<StatsDto['byMonth'][number] | null>(
    (best, item) => (!best || item.flights > best.flights ? item : best),
    null,
  );
  if (month && month.flights > 1) {
    rows.push({
      label: 'Самый плотный месяц',
      value: `${MONTHS_FULL[Number(month.month.slice(5, 7)) - 1]} ${month.month.slice(0, 4)}`,
      meta: `${month.flights} ${plural(month.flights, ['рейс', 'рейса', 'рейсов'])} · ${formatDistance(month.distanceKm)}`,
    });
  }

  let gap = 0;
  let gapFrom: FlightDto | null = null;
  let gapTo: FlightDto | null = null;
  for (let index = 1; index < past.length; index += 1) {
    const before = past[index - 1]!;
    const after = past[index]!;
    const days = Math.floor(
      (new Date(after.scheduledDepUtc).getTime() - new Date(before.scheduledDepUtc).getTime()) / 86_400_000,
    );
    if (days > gap) {
      gap = days;
      gapFrom = before;
      gapTo = after;
    }
  }
  if (gapFrom && gapTo && gap > 0) {
    rows.push({
      label: 'Самая долгая пауза',
      value: pause(gap),
      meta: `${dayMonthYear(gapFrom)} — ${dayMonthYear(gapTo)}`,
    });
  }

  return rows;
}

/** Рекорды; строки с рейсом открывают его карточку. */
export function Records({
  stats,
  flights,
  onSelectFlight,
}: {
  stats: StatsDto;
  flights: FlightDto[];
  onSelectFlight: (flight: FlightDto) => void;
}) {
  const rows = records(stats, flights);
  if (rows.length === 0) return null;

  return (
    <section className="section">
      <h2 className="section-title">Рекорды</h2>
      <div className="records">
        {rows.map((row) => {
          const body = (
            <>
              <span className="records-label">{row.label}</span>
              <b className="records-value">{row.value}</b>
              <span className="records-meta">{row.meta}</span>
            </>
          );
          return row.flight ? (
            <button
              type="button"
              className="records-row"
              key={row.label}
              onClick={() => {
                haptic();
                onSelectFlight(row.flight!);
              }}
            >
              {body}
              <i className="records-arrow">›</i>
            </button>
          ) : (
            <div className="records-row" key={row.label}>
              {body}
            </div>
          );
        })}
      </div>
    </section>
  );
}
